import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { fetchCompanyData } from '~/data/company';
import { Modules } from '~/data/modules';
import { UseSWROutput, useSWR } from '~/hooks/useSWR';
import { useAppModules } from '~/scenes/AppModules/useAppModules';
import { MyWorkspaceUserSettingsKey } from '~/scenes/MyWorkspace/userSettings';
import { PreferencesApiClient } from '~/services/Http';
import { AppDispatch } from '~/store';

type UserPreferences = {
  useWorkspaceAsHomepage?: boolean;
  [p: string]: unknown;
};

export const useGetUserPreferencesData = (
  isDisabled?: boolean,
): UseSWROutput<UserPreferences> & {
  hasData: boolean;
} => {
  const preferencesKey = MyWorkspaceUserSettingsKey;

  const results = useSWR(
    !isDisabled ? ['userPreferences', preferencesKey] : null,
    () =>
      PreferencesApiClient.getPreferences([preferencesKey]).then((r) => {
        return r.data[preferencesKey] ?? {};
      }),
  );

  return {
    ...results,
    hasData: typeof results.data !== 'undefined',
  };
};

export const useUserHomepageSetting = () => {
  const dispatch: AppDispatch = useDispatch();

  const {
    isModuleActive,
    isLoading: isLoadingAppModules,
    hasData: hasAppModulesData,
  } = useAppModules();

  const getInitializationStatus = useCallback((isLoading, hasData) => {
    return {
      notInitialized: !hasData && !isLoading,
      isInitializing: !hasData && isLoading,
      initialized: hasData,
    };
  }, []);

  const { modules, checkUserPreferences } = useMemo(() => {
    const modules = getInitializationStatus(
      isLoadingAppModules,
      hasAppModulesData,
    );

    return {
      modules,
      checkUserPreferences:
        modules.initialized && isModuleActive(Modules.WORKSPACE),
    };
  }, [
    isLoadingAppModules,
    hasAppModulesData,
    getInitializationStatus,
    isModuleActive,
  ]);

  useEffect(() => {
    if (modules.notInitialized) {
      dispatch(fetchCompanyData());
    }
  }, [modules, dispatch]);

  const {
    data,
    isLoading: isLoadingUserPreferences,
    hasData: hasUserPreferencesData,
  } = useGetUserPreferencesData(!checkUserPreferences);

  return useMemo(() => {
    if (checkUserPreferences) {
      const userPreferences = getInitializationStatus(
        isLoadingUserPreferences,
        hasUserPreferencesData,
      );

      const initialized = modules.initialized && userPreferences.initialized;
      const notInitialized =
        modules.notInitialized && userPreferences.notInitialized;

      return {
        useWorkspaceAsHomepage: data?.useWorkspaceAsHomepage ?? false,
        initialized,
        isInitializing: !notInitialized && !initialized,
        notInitialized,
      };
    }

    return {
      useWorkspaceAsHomepage: false,
      ...modules,
    };
  }, [
    modules,
    isLoadingUserPreferences,
    data,
    hasUserPreferencesData,
    getInitializationStatus,
    checkUserPreferences,
  ]);
};
